/**
 * The per-invoice QuickBooks panel (QBO API v2, slice 6b).
 *
 * The founder's 2026-08-25 ruling made the BATCH the primary push and
 * kept the per-invoice one, because an operator has to be able to retry
 * a single invoice and read why it failed. This panel is that, plus the
 * thing a batch report cannot be: an invoice's STANDING push state, days
 * after the click.
 *
 * IT CARRIES THE #90 WARNING. Olive never voids in QuickBooks (founder
 * ruling: create-only push, read-check and warn). So when an invoice is
 * voided here and a live copy still sits in QuickBooks, this is where the
 * operator finds out — including the stranded-payment case that built the
 * unapplied-cash pile. A warning shown only at push time would never be
 * seen, because the void happens later, which is the entire problem.
 *
 * A voided invoice is never offered Send. There is nothing to send, and
 * the affordance would invite exactly the wrong act.
 */

import { type ReactNode } from 'react';
import { apiErrorFrom } from '@/lib/api';
import { invalidateInvoiceReads } from '@/lib/invoiceInvalidation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canPushInvoice } from '@/lib/capabilities';
import { qboApi, qboReasonSentence } from '@/lib/qboApi';
import {
  ALREADY_PUSHED_REMEDY_SENTENCE,
  pushFailureRemedy,
  pushStatusCopy,
} from '@/lib/qboPushStatus';
import { formatTimestamp } from '@/lib/format';
import { contactSentence, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { Button } from '@/components/primitives/Button';

export interface QboPushAttemptWire {
  attemptId?: string;
  status: string;
  qboInvoiceId?: string | null;
  /** When the create landed at Intuit. Only pushed attempts carry it,
   *  and older rows may not: the verified sentence dates itself when it
   *  can and stays silent about time when it cannot. */
  qboPushedAt?: string | null;
  lastError?: string | null;
}

export interface QboVoidCheckWire {
  state: string;
  warnings: string[];
  messages: string[];
  /** Whether a human has already confirmed a vanished copy was removed on
   *  purpose. Filled in by the handler from Olive's stored row: Intuit
   *  has no opinion about a signature Olive holds. */
  acknowledged?: boolean;
  /** The no-answer copy search (0214): the document number searched for,
   *  and for copy_lookup_not_found when QuickBooks was searched (ISO). */
  lookupDocNumber?: string;
  lookupCheckedAt?: string;
}

export interface QboPushStateWire {
  invoiceId: string;
  status: string;
  qboInvoiceId: string | null;
  attempts: QboPushAttemptWire[];
  void_check: QboVoidCheckWire | null;
}

export interface QboPushPanelProps {
  invoiceId: string;
  /** The Olive invoice's own status. Only issued and void invoices have
   *  anything to say here: a draft has no frozen snapshot to send. */
  invoiceStatus: string;
}

async function readPushState(slug: string, invoiceId: string, signal?: AbortSignal) {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/invoices/${encodeURIComponent(invoiceId)}/qbo-push`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) throw await apiErrorFrom(res, `GET ${path}`);
  return (await res.json()) as QboPushStateWire;
}

/** The panel's sentence for the no-answer copy search, or null when the
 *  check is not one of its states. Exported for the screen tests. */
export function copyLookupSentence(check: QboVoidCheckWire | null | undefined): string | null {
  if (check == null) return null;
  const doc = check.lookupDocNumber != null && check.lookupDocNumber !== '' ? ` #${check.lookupDocNumber}` : '';
  switch (check.state) {
    case 'copy_lookup_pending':
      return "The send got no answer, so a copy may be in QuickBooks. It's being checked now, so wait a minute before you send it again.";
    case 'copy_lookup_unavailable':
      return "The send got no answer, and QuickBooks couldn't be searched for a copy. Look for this invoice in QuickBooks before you send it again.";
    case 'copy_lookup_ambiguous':
      return `QuickBooks has more than one open invoice${doc} for this client. Check there and void the extra copies.`;
    case 'copy_lookup_not_found': {
      const when = check.lookupCheckedAt != null ? ` on ${formatTimestamp(check.lookupCheckedAt)}` : '';
      return `The send got no answer. A search of QuickBooks${when} found no open copy of invoice${doc}.`;
    }
    default:
      return null;
  }
}

function stateSentence(state: QboPushStateWire, contact: SupportContact): string {
  const id = state.qboInvoiceId;
  if (id !== null && id !== undefined) {
    // F3: presence is never asserted over a report of absence. The copy
    // check outranks the sticky push id: a copy Olive OBSERVED gone, and
    // a copy Olive COULD NOT READ, are two different sentences, and
    // neither may say "In QuickBooks" in the present tense. Both name
    // the document, because the act each asks for is "go and look at
    // THIS one".
    const check = state.void_check?.state;
    if (check === 'missing_in_qbo') {
      return `QuickBooks invoice ${id} is no longer in QuickBooks. Check there that it was removed on purpose.`;
    }
    if (check === 'check_unavailable') {
      return `QuickBooks invoice ${id} couldn't be read, so where it stands isn't known. Open it in QuickBooks to check.`;
    }
    // The other three verdicts get the same observed-at-last-look
    // discipline (TRUTH-FIX train): the check's tense, never the id's.
    // already_void arrives with empty messages[], so without its own
    // sentence the panel said only "In QuickBooks as invoice N." about
    // a document Olive saw closed.
    if (check === 'already_void') {
      return `Sent to QuickBooks as invoice ${id}. At the last check it was already void there, so nothing more is needed.`;
    }
    // open and open_with_payment render the #90 warning below this
    // line; the sentence must agree with it, not contradict it. The
    // payment fact stays in the warning's own message.
    if (check === 'open' || check === 'open_with_payment') {
      return `Sent to QuickBooks as invoice ${id}. At the last check it was still open there.`;
    }
    // No copy-check verdict outranks the id: the sentence carries the
    // TOTAL verdict recorded on the pushed attempt, from the same ruled
    // map the invoices list renders, so the two surfaces cannot drift.
    // total_mismatch and total_unverifiable are pushes (the document is
    // at Intuit; a re-send would duplicate it), but neither may read as
    // the verified sentence: compared-and-wrong, never-compared, and
    // compared-and-matching are three different claims.
    const pushed = [...state.attempts].reverse().find((a) => a.status === 'pushed');
    const verdict = pushed?.lastError ?? null;
    if (verdict === 'total_mismatch' || verdict === 'total_unverifiable') {
      return `In QuickBooks as invoice ${id}. ${pushStatusCopy('pushed', verdict, id, contact).detail}`;
    }
    const sentAt = pushed?.qboPushedAt;
    return sentAt != null
      ? `In QuickBooks as invoice ${id}, sent ${formatTimestamp(sentAt)}. Total checked and matches.`
      : `In QuickBooks as invoice ${id}. Total checked and matches.`;
  }
  // THE NO-ANSWER COPY SEARCH (0214). A void whose send got no answer
  // has no id to name, so the sentence names the document number Olive
  // searched for instead.
  const lookup = copyLookupSentence(state.void_check);
  if (lookup !== null) return lookup;
  if (state.status === 'failed') {
    // F1: the class owns the WHOLE sentence, from the same map the
    // invoices list renders. The old hardcoded "Not in QuickBooks."
    // prefix asserted a non-delivery half the classes cannot know.
    const last = state.attempts[state.attempts.length - 1];
    return pushStatusCopy('failed', last?.lastError ?? null, null, contact).detail;
  }
  if (state.status === 'pending') {
    return 'A send is in progress.';
  }
  return 'Not in QuickBooks yet.';
}

/**
 * The invoice's standing push state, keyed and fetched ONE way. Shared
 * with InvoicePreview (the void dialog needs the sticky qboInvoiceId):
 * one queryKey + queryFn pair means React Query dedupes the wire read to
 * a single fetch however many surfaces subscribe, and no second caller
 * can drift the key shape or re-implement the read.
 */
export function useQboPushState(invoiceId: string, invoiceStatus: string) {
  const { tenantSlug } = useTenant();
  const relevant = invoiceStatus === 'issued' || invoiceStatus === 'void';
  return useQuery<QboPushStateWire>({
    queryKey: ['qbo', 'push-state', tenantSlug, invoiceId],
    queryFn: ({ signal }) => readPushState(tenantSlug, invoiceId, signal),
    enabled: relevant,
  });
}

export function QboPushPanel({ invoiceId, invoiceStatus }: QboPushPanelProps): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const contact = useSupportContact();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // push_invoice, NOT manage_billing_integration (founder ruling
  // 2026-08-26). Sending an issued invoice is the bookkeeper's job;
  // connecting and disconnecting QuickBooks stays owner-only and lives on
  // the QuickBooks card. Asking the wrong question here hides the batch
  // from the person who actually runs month end.
  const canPush = canPushInvoice(whoami);
  const relevant = invoiceStatus === 'issued' || invoiceStatus === 'void';

  const stateQuery = useQboPushState(invoiceId, invoiceStatus);

  const ackMut = useMutation({
    mutationFn: () => qboApi.acknowledgeCopyRemoval(tenantSlug, invoiceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['qbo', 'push-state', tenantSlug, invoiceId],
      });
      // Widened through the shared helper: the list invalidation's prefix
      // never reaches the sibling unbilled key.
      invalidateInvoiceReads(queryClient, tenantSlug);
    },
  });

  const pushMut = useMutation({
    mutationFn: () => qboApi.push(tenantSlug, invoiceId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['qbo', 'push-state', tenantSlug, invoiceId],
      });
    },
  });

  if (!relevant) return null;

  const state = stateQuery.data;
  const inQuickBooks = state?.qboInvoiceId !== null && state?.qboInvoiceId !== undefined;
  // Send is offered for an ISSUED invoice that is not already there. A
  // voided invoice is never offered it: Olive is create-only in
  // QuickBooks, so there is nothing to send and the affordance would
  // invite the wrong act. Nor while a wire attempt is PENDING: the
  // sentence already says a send is in progress, and a second Send
  // beside it invites the double-create the idempotency key cannot
  // promise to absorb (its secret is rotatable).
  const offerSend =
    canPush &&
    invoiceStatus === 'issued' &&
    state !== undefined &&
    !inQuickBooks &&
    state.status !== 'pending';
  // F12, belt to the server's brace: the #90 warning's messages all say
  // "voided here", so they may only ever render on an invoice that IS
  // void. The fixed GET handler answers void_check: null for any other
  // status, but a wire value that should be impossible must still not
  // put the word "voided" on an issued invoice beside a Send button.
  // The class's own ruling about WHERE its fix lives (the registry, not a
  // list kept here). Only a FAILED state can carry one: a pushed or
  // pending invoice has nothing to go and configure.
  const remedy =
    state?.status === 'failed'
      ? pushFailureRemedy(state.attempts[state.attempts.length - 1]?.lastError ?? null)
      : undefined;

  const voidWarning =
    invoiceStatus === 'void' && state?.void_check && state.void_check.messages.length > 0
      ? state.void_check
      : null;

  // THE ONE STATE THAT NEVER CLEARS ITSELF. Every other outcome resolves
  // on its own: the next check sees the copy void, or open, and the row
  // moves. A copy that is NOT THERE answers the same way forever, so
  // without a human saying "yes, we removed that on purpose" the invoice
  // counts against month end for the rest of its life with nothing anyone
  // can do. This button is the off switch, and it is deliberately offered
  // for that state ALONE: on an open copy it would silence an invoice
  // that can still take a family's money.
  const copyIsMissing = state?.void_check?.state === 'missing_in_qbo';
  const alreadyConfirmed = state?.void_check?.acknowledged === true;
  const offerRemovalAck = canPush && copyIsMissing && !alreadyConfirmed;

  return (
    <section
      data-testid="qbo-push-panel"
      className="border border-sage-200 bg-sage-50 rounded-card p-3 sm:p-4 space-y-2"
    >
      <h3 className="font-display text-base text-sage-800">QuickBooks</h3>

      <p data-testid="qbo-push-state" className="text-sm text-ink-soft">
        {stateQuery.isPending
          ? 'Checking QuickBooks…'
          : stateQuery.isError
            ? contactSentence(
                "This invoice couldn't be checked in QuickBooks. Reload the page, and if it happens again, tell {contact_billing}.",
                contact,
              )
            : stateSentence(state as QboPushStateWire, contact)}
      </p>

      {(offerSend || remedy !== undefined) && (
        <div className="flex flex-wrap items-center gap-2">
          {/* THE FIRST STEP FIRST. These classes' sentences name an act in
              the QuickBooks settings and then "send again", so the remedy
              leads and Send steps back to secondary; where sending IS the
              act there is no remedy and Send keeps the primary weight.
              The destination is the settings view itself, where the item
              map and Match clients both live. */}
          {remedy !== undefined && (
            <Button
              data-testid="qbo-push-remedy"
              onClick={() => navigate(`/t/${tenantSlug}/admin-review?tab=quickbooks`)}
            >
              Open QuickBooks settings
            </Button>
          )}
          {offerSend && (
            <Button
              data-testid="qbo-push-send"
              variant={remedy === undefined ? 'primary' : 'secondary'}
              onClick={() => pushMut.mutate()}
              loading={pushMut.isPending}
            >
              Send to QuickBooks
            </Button>
          )}
        </div>
      )}

      {/* F3: already_pushed is the wrong-customer flow's dead end — the
          operator relinked the client and pressed Send, and QuickBooks
          already holds the document under the OLD link. Not an error
          (role=status): the remedy is the message. First-push success
          ('pushed') deliberately renders nothing here. */}
      {pushMut.data?.outcome === 'already_pushed' && (
        <p data-testid="qbo-push-already" role="status" className="text-sm text-ink-soft">
          {ALREADY_PUSHED_REMEDY_SENTENCE}
        </p>
      )}

      {pushMut.data && pushMut.data.outcome !== 'pushed' && pushMut.data.outcome !== 'already_pushed' && (
        <p data-testid="qbo-push-result" role="alert" className="text-sm text-pepper">
          {pushMut.data.outcome === 'pushed_total_mismatch'
            ? "This reached QuickBooks, but the total there doesn't match. Compare the two in QuickBooks, because sending again won't fix it."
            : qboReasonSentence(pushMut.data.reason, contact)}
        </p>
      )}

      {(offerRemovalAck || alreadyConfirmed || ackMut.isSuccess) && (
        <div className="space-y-1">
          {offerRemovalAck && !ackMut.isSuccess && (
            <Button
              data-testid="qbo-copy-removal-ack"
              variant="ghost"
              onClick={() => ackMut.mutate()}
              loading={ackMut.isPending}
            >
              {/* Past tense on purpose. The operator is REPORTING a
                  removal they verified in QuickBooks, not asking Olive to
                  delete anything: Olive never writes across this seam. */}
              Confirm the copy was removed
            </Button>
          )}
          {(alreadyConfirmed || ackMut.isSuccess) && (
            <p data-testid="qbo-copy-removal-ack-result" role="alert" className="text-sm text-ink-soft">
              {/* The same sentence the invoices list shows for this row,
                  so two surfaces cannot drift about one fact. */}
              Confirmed as deliberately removed from QuickBooks.
            </p>
          )}
          {ackMut.isError && (
            <p data-testid="qbo-copy-removal-ack-result" role="alert" className="text-sm text-pepper-deep">
              The QuickBooks copy changed while this page was open, so nothing was confirmed. Open
              the invoice again to see the latest.
            </p>
          )}
        </div>
      )}

      {voidWarning && (
        <div
          data-testid="qbo-void-warning"
          role="alert"
          className="space-y-1 border border-pepper/30 bg-pepper-soft rounded-tight p-2"
        >
          {/* The messages come from the server as operator copy carrying
              no amounts, doc numbers, or QBO ids. Rendered verbatim: this
              is the one place the ruled #90 warning reaches a person. */}
          {voidWarning.messages.map((m) => (
            <p key={m} className="text-sm text-pepper-deep">
              {m}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}
